import { pressSharedKey, saySharedAgent, stopSharedAgent, uploadSharedFile, type SharedKey } from '../transport/relay'
import { addSharedEcho, kickShared } from './sharedSession'

/**
 * THE COMPOSER'S SEND, FOR A FRIEND'S AGENT — the words and any attached files, through the share
 * (routes_agent_share). Each file goes up the owner's own upload lane, asked of their machine by
 * the cloud; the message names the files by the ids the cloud handed back, never by a path.
 *
 * `kept` are files that went up before an earlier press failed to send: the tray had already let
 * them go, so they ride with this one rather than being lost. `uploaded` reports every file id
 * held so far after each upload, so a failure part-way leaves the caller holding all of them.
 *
 * Throws what the cloud refused (shareErrText reads it). On success the reader's words are drawn
 * at once (the echo) and whoever draws this share reads again.
 */
export async function sendToShared(
  id: string,
  text: string,
  items: { id: string; file: File }[],
  kept: string[],
  on: { progress: (itemId: string, pct: number) => void; uploaded: (itemId: string, held: string[]) => void },
  /** Phase 2: the reader's own agents @-pinged in `text` (the cloud links each, proven theirs). */
  pings: { hash: string; device_id: string }[] = [],
): Promise<{ queued: boolean }> {
  const files = [...kept]
  for (const item of items) {
    const up = await uploadSharedFile(id, item.file, (pct) => on.progress(item.id, pct))
    files.push(up.file_id)
    on.uploaded(item.id, [...files])
  }
  const r = await saySharedAgent(id, text, files, pings)
  if (text) addSharedEcho(id, text)
  kickShared(id)
  return { queued: r.queued }
}

/** One key of the keys row, for a friend the sharer gave the keys; the conversation reads again. */
export async function pressShared(id: string, key: SharedKey): Promise<void> {
  await pressSharedKey(id, key)
  kickShared(id)
}

/** The friend's Stop, as the status line says it. */
export async function stopShared(id: string): Promise<string> {
  const r = await stopSharedAgent(id)
  kickShared(id)
  if (r.sent) return 'Stop sent to the agent'
  return r.why === 'not_working' ? 'It had already stopped' : 'Stop was just sent'
}
